<template>
    <div>
        <div :class="['bordered', translationStateClass, focusedClass]"
             id="tareget-container">

            <div class="editor">
                <TargetSegmentEditor v-model="translationSegment"
                                     :setFocus="updateFocus"
                                     :focused="segmentItem.focused"
                                     :disableFormatting="disableFormatting"
                                     @focus="focusAction"
                                     @typing-started="onTypingStarted"/>
            </div>

            <ButtonTemplate id="clear-button"
                            class="clear-icon"
                            :disabled="!showClear"
                            :tooltipMessage="eraceTranslationTooltip"
                            @buttonClick="doClearTranslation">

                <span class="material-symbols-outlined">
                    cancel
                </span>

            </ButtonTemplate>

        </div>


    </div>

</template>

<script lang="ts">
    import { defineComponent } from 'vue';

    import TargetSegmentEditor from './TargetSegmentEditor.vue';
    import ButtonTemplate from '@/components/ButtonTemplate.vue';

    import { TranslationSegment } from '@/models/TranslationSegment';
    import { SegmentEditorItem } from '@/models/SegmentEditorItem';

    import { shortcuts } from '@/utils/shortcuts';
    import { translationSegmentService } from './translationSegmentService'
    import { segmentTextLogicService } from '@/services/translationSegment/segmentTextLogicService';

    export default defineComponent({
        emits: [
            'input',
            'typing-started'
        ],
        props: {
            modelValue: Object,
            disableFormatting: {
                type: Boolean,
                default: false
            },
        },
        components: {
            TargetSegmentEditor,
            ButtonTemplate,
        },
        watch: {
            async focused(newVal, oldVal) {
                if (newVal && !oldVal) {
                    // Small delay to allow for rapid Tab navigation to settle
                    this.pendingTranslation = true;
                    await this.$nextTick();
                    
                    // Check if still focused after nextTick
                    if (this.focused && this.pendingTranslation) {
                        await this.goToEditField();
                    }
                    this.pendingTranslation = false;
                }
            },
        },
        data() {
            return {
                eraceTranslationTooltip: shortcuts.getTooltipTranslated(this.$t, shortcuts.discardTranslation),

                translationSegmentService: translationSegmentService.getInstance(),
                segmentTextLogicService: segmentTextLogicService.getInstance(),
                updateFocus: false,
                pendingTranslation: false,
                hasEmittedTyping: false,
            }
        },
        mounted() {
            if (this.segmentItem.focused) {
                this.setTargetFocus();  // This triggers the setFocus chain
            }
        },
        computed: {
            segmentItem: {
                get(): SegmentEditorItem { return this.modelValue as SegmentEditorItem; },
                set(value: SegmentEditorItem) { this.$emit('input', value); }
            },
            translationSegment: {
                get(): TranslationSegment { return this.segmentItem.data as TranslationSegment; },
                set(value: TranslationSegment) { this.segmentItem.data = value }
            },
            focused(): boolean{
                return this.segmentItem.focused;
            },
            showClear(): boolean {
                return this.focused &&
                    !this.translationSegmentService.currentTargetIsEmpty();
            },
            translationStateClass(): string {
                if (this.translationSegment.draftData.text.trim() == '') {
                    return 'empty';
                }

                if (this.translationSegment.segmentTranslation == null) {
                    return 'editing';
                }

                if (this.translationSegment.segmentTranslation.matchPercent < 100) {
                    return 'editing';
                }

                return this.segmentTextLogicService.translationReady(this.translationSegment) ?
                    'empty' : 'editing';
            },
            focusedClass(): string {
               if(!this.segmentItem.focused){
                   return 'editor-not-focused'
               }

               return ''
            },
        },
        created() {
            if(!this.segmentItem.focused)
                return;
  
            this.translateIfRequired() 
        },
        methods: {
            goToEditField() {
                this.setTargetFocus();
            },
            setTargetFocus() {
                this.updateFocus = !this.updateFocus;
                this.translationSegmentService.setFocusToSegment(this.translationSegment.translationSegmentId);
            },
            
            translateIfRequired(){
                 if (this.translationSegment.translationVariants)
                    return;

                this.doSegmentTranslation(); 
            },
            doSegmentTranslation() {
                this.translationSegmentService.translate(this.translationSegment.translationSegmentId);
            },

            focusAction() {
                this.hasEmittedTyping = false;
                this.translationSegmentService.setFocusToSegment(this.translationSegment.translationSegmentId);
                this.translateIfRequired()
            },
            doClearTranslation() {
                if (this.showClear) {
                    this.translationSegmentService.cleanTranslationSegment(this.translationSegment.translationSegmentId)
                }
            },
            onTypingStarted() {
                if (!this.hasEmittedTyping) {
                    this.$emit('typing-started');
                    this.hasEmittedTyping = true;
                }
            },
        }
    });
</script>

<style lang="scss" scoped>

    .bordered {
        width: 445px;
        margin-right: 35px;
        padding: 6px 3px;
        text-align: justify;
        border: 1px solid var(--text-color);
        border-radius: 10px;
        background-color: white;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .editor {
            width: 410px;
            height: 100%;
        }
    }

    .empty {
        background-color: var(--editor-white);
    }

    .editing {
        background-color: var(--editor-ellow);
    }

    .editor-not-focused > * {
        & :deep().ql-editor {
            cursor: pointer;
        }

        & :deep().ql-editor > * {
            cursor: pointer;
        }
    }

    .clear-icon {
        cursor: pointer;
        margin-left: 15px;
        margin-right: 15px;
        width: 24px;
        height: 24px;
    }
</style>
